import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { APP_RUNTIME_CONFIG } from '@core/auth/app-config.model';

import { API_ERROR_HANDLED } from './api-error.interceptor';
import {
  CertificationDto,
  EmploymentTypeDto,
  SaveCertificationDto,
  SaveEmploymentTypeDto,
  SaveSoftwareToolDto,
  SoftwareToolDto,
} from './catalogs.dto';

/**
 * The two workforce catalogues that are callable today
 * (`backend-contract-workforce-response.md` §4).
 *
 * Both are open to any signed-in caller: a catalogue is what the company
 * recognises, and the people who need to read it are not the people who maintain
 * it. Writing software is admin.
 *
 * NOTE what is missing: the contract lists no write for certifications, only the
 * two reads. So the certification editor stays local until there is somewhere to
 * send it — wiring a `POST` to a path nobody has published would turn a working
 * screen into a 404.
 */
@Injectable({ providedIn: 'root' })
export class CatalogsApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(APP_RUNTIME_CONFIG);

  /** Whether a backend is configured. Empty `apiUrl` = no API yet. */
  available(): boolean {
    return !!this.config.apiUrl;
  }

  private url(path: string): string {
    return `${this.config.apiUrl.replace(/\/$/, '')}${path}`;
  }

  listCertifications(): Observable<CertificationDto[]> {
    return this.http.get<CertificationDto[]>(this.url('/certifications'));
  }

  /**
   * Add a certification. Answers the WHOLE catalogue, not the new row, so the
   * caller replaces its list rather than splicing one in.
   */
  createCertification(body: SaveCertificationDto): Observable<CertificationDto[]> {
    return this.http.post<CertificationDto[]>(this.url('/certifications'), body);
  }

  /** Edit one. Same body as the create, and its links are replaced wholesale. */
  updateCertification(
    certificationGuid: string,
    body: SaveCertificationDto,
  ): Observable<CertificationDto[]> {
    return this.http.patch<CertificationDto[]>(
      this.url(`/certifications/${encodeURIComponent(certificationGuid)}`),
      body,
    );
  }

  /**
   * Retire a certification — a SOFT delete, and it answers the whole catalogue.
   *
   * The row survives for the audit trail only: every read filters it out, so it
   * leaves the catalogue and anybody recorded as holding it loses that credential
   * from their page. Its name is freed for reuse, because nothing can see a
   * deleted row and so could never collide with one.
   */
  retireCertification(certificationGuid: string): Observable<CertificationDto[]> {
    return this.http.delete<CertificationDto[]>(
      this.url(`/certifications/${encodeURIComponent(certificationGuid)}`),
    );
  }

  /**
   * The employment types a person can be engaged on.
   *
   * Its own root path rather than something under `/company`, which is where the
   * rest of these catalogues live — it is `/api/v1/employment-types`.
   */
  listEmploymentTypes(): Observable<EmploymentTypeDto[]> {
    return this.http.get<EmploymentTypeDto[]>(this.url('/employment-types'));
  }

  createEmploymentType(body: SaveEmploymentTypeDto): Observable<EmploymentTypeDto> {
    return this.http.post<EmploymentTypeDto>(this.url('/employment-types'), body);
  }

  updateEmploymentType(
    employmentTypeGuid: string,
    body: SaveEmploymentTypeDto,
  ): Observable<EmploymentTypeDto> {
    return this.http.patch<EmploymentTypeDto>(
      this.url(`/employment-types/${encodeURIComponent(employmentTypeGuid)}`),
      body,
    );
  }

  /**
   * Delete an employment type — the typo case, not the retire case.
   *
   * **204, or 409 when something still points at it**: people engaged on it, or
   * seats defined with it. That second clause is the one that surprises — a row
   * with nobody on it looks unused from the people side while every position
   * naming it would be left pointing at nothing. The 409's `detail` names the way
   * out, so it is shown rather than translated.
   *
   * Retiring is the other operation: `PATCH` with `active: false`, which KEEPS the
   * row and stops offering it. Delete releases the name for immediate reuse.
   */
  deleteEmploymentType(employmentTypeGuid: string): Observable<void> {
    return this.http.delete<void>(
      this.url(`/employment-types/${encodeURIComponent(employmentTypeGuid)}`),
    );
  }

  /** Delete a software tool. 409 where somebody has access, or a seat requires it. */
  deleteSoftware(softwareGuid: string): Observable<void> {
    return this.http.delete<void>(this.url(`/software/${encodeURIComponent(softwareGuid)}`));
  }

  listSoftware(): Observable<SoftwareToolDto[]> {
    return this.http.get<SoftwareToolDto[]>(this.url('/software'));
  }

  /** `handled`: the add/edit modal stays open and shows the refusal itself. */
  createSoftware(body: SaveSoftwareToolDto): Observable<SoftwareToolDto> {
    return this.http.post<SoftwareToolDto>(this.url('/software'), body, handled());
  }

  updateSoftware(softwareGuid: string, body: SaveSoftwareToolDto): Observable<SoftwareToolDto> {
    return this.http.patch<SoftwareToolDto>(this.url(`/software/${softwareGuid}`), body, handled());
  }
}

function handled(): { context: HttpContext } {
  return { context: new HttpContext().set(API_ERROR_HANDLED, true) };
}
